import { getLastGlsResponseJackpotMode } from '@/game-client/gls-jackpot-mode-tracker';
import {
  getGlsLaunchGeneration,
  isGlsLaunchGenerationCurrent,
  registerGlsLaunchInvalidateListener,
} from '@/game-client/gls-launch-guard';
import { attemptDeliverGlsInitializeUrl } from '@/game-client/gls-pending-initialize-url';
import {
  isJackpotModuleRenderable,
  shouldDeliverJackpotReconPrewarm,
} from '@/features/SWJ/helpers/jackpot-mode-launch';
import {
  runJackpotGlsReconcile,
  type FetchGlsGame,
  type JackpotGlsReconcileOptions,
} from '@/game-client/jackpot-gls-reconcile-run';
import { useStore } from '@/store/useStore';
import {
  isJackpotReconPrewarmAwaitingContext,
  markJackpotReconPrewarmDeliveryReady,
} from '@/utilities/logger/datadog/vitals';
import {
  ddNotifyJackpotReconPostponed,
  ddNotifyJackpotReconStart,
} from '@/utilities/logger/datadog/rumEvents/jackpotRecon';

let prewarmContextUnsubscribe: (() => void) | null = null;

export const cancelDeferredJackpotGlsReconcile = (): void => {
  prewarmContextUnsubscribe?.();
  prewarmContextUnsubscribe = null;
};

/** Same prewarm delivery step as {@link useJackpotModeGlsReconcile} (hook may not re-run on URL change). */
const ensureJackpotReconPrewarmDelivery = (): void => {
  const { clientStatus, gameStatus } = useStore.getState();
  if (!clientStatus.loaded || gameStatus.jackpotReconcileRunning) {
    return;
  }

  const shouldDeliverPrewarm = shouldDeliverJackpotReconPrewarm({
    glsResponseJackpotMode: getLastGlsResponseJackpotMode(),
    canRenderJackpotModule: isJackpotModuleRenderable(),
  });
  if (!shouldDeliverPrewarm || gameStatus.jackpotGlsPrewarmContextReceived) {
    return;
  }

  if (!isJackpotReconPrewarmAwaitingContext()) {
    markJackpotReconPrewarmDeliveryReady();
  }
  attemptDeliverGlsInitializeUrl();
};

/**
 * User-initiated jackpot reconcile: runs after {@link GameStatus.jackpotGlsPrewarmContextReceived}
 * (first initialize URL applied). Auto recon uses the same latch via the reconcile hook.
 */
export const runJackpotGlsReconcileAfterPrewarmContext = (
  fetchGame: FetchGlsGame,
  urlGameUid?: string | null,
  options: JackpotGlsReconcileOptions = {},
): void => {
  cancelDeferredJackpotGlsReconcile();
  const launchGeneration = getGlsLaunchGeneration();

  const startReconcile = (): void => {
    if (!isGlsLaunchGenerationCurrent(launchGeneration)) {
      cancelDeferredJackpotGlsReconcile();
      return;
    }
    cancelDeferredJackpotGlsReconcile();
    if (useStore.getState().gameStatus.jackpotReconcileRunning) {
      return;
    }

    ddNotifyJackpotReconStart(false, {
      urlGameUid,
      options,
    });
    void runJackpotGlsReconcile(fetchGame, urlGameUid, options);
  };

  ensureJackpotReconPrewarmDelivery();

  if (useStore.getState().gameStatus.jackpotGlsPrewarmContextReceived) {
    startReconcile();
    return;
  }

  ddNotifyJackpotReconPostponed();

  prewarmContextUnsubscribe = useStore.subscribe((state, prevState) => {
    if (!isGlsLaunchGenerationCurrent(launchGeneration)) {
      cancelDeferredJackpotGlsReconcile();
      return;
    }
    const received = state.gameStatus.jackpotGlsPrewarmContextReceived;
    const prevReceived = prevState.gameStatus.jackpotGlsPrewarmContextReceived;
    if (received && !prevReceived) {
      startReconcile();
    }
  });
};

registerGlsLaunchInvalidateListener(cancelDeferredJackpotGlsReconcile);
